import { useAPI } from "@/hooks/useAPI";
import { CaseImagesService } from "@/services/caseImagesService";
import type { CaseImageMeta } from "@/types/case";
import { getErrorMessage } from "@/utils/errorUtils";
import { useCallback, useEffect, useRef } from "react";
import useSWR from "swr";

const PAGE_SIZE = 50;
const ONE_HOUR_MS = 60 * 60 * 1000;

// On refocus this long after the last mint, re-mint the URL map (the API gives no per-image expiry).
const URLS_STALE_AFTER_MS = ONE_HOUR_MS - 60_000;

const CASE_IMAGES_META_KEY = "/cases/images/meta";
const CASE_SIGNED_URLS_KEY = "/cases/images/signed-urls";

export const useCaseImages = (caseId: string | undefined) => {
  const api = useAPI("v2");
  const metaCacheKey = caseId ? [CASE_IMAGES_META_KEY, caseId] : null;

  // 1. Fetch images metadata first — return immediately so we can show skeleton grid
  const {
    data: images,
    error,
    isLoading,
  } = useSWR<CaseImageMeta[]>(
    metaCacheKey,
    async () => {
      if (!caseId) return [];
      const service = new CaseImagesService(api);
      return service.getImages(caseId);
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: ONE_HOUR_MS,
    }
  );

  const safeImages = Array.isArray(images) ? images : [];
  const urlsCacheKey = caseId && safeImages.length > 0 ? [CASE_SIGNED_URLS_KEY, caseId] : null;

  // 2. Fetch signed URLs — cached by SWR, populated progressively
  const { data: urls = {}, mutate } = useSWR<Record<string, string>>(urlsCacheKey, () => ({}), {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: ONE_HOUR_MS,
  });

  const urlsFetchedAtRef = useRef(0);
  const fetchingRef = useRef(false);
  const retriedImageIdsRef = useRef<Set<string>>(new Set());

  // (Re)mint signed URLs for every image, overwriting the cached map in place (no skeleton flash).
  const refreshSignedUrls = useCallback(async () => {
    if (!caseId || fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const service = new CaseImagesService(api);
      for (let page = 0; ; page++) {
        const { items, hasMore } = await service.getImagesWithSignedUrls(caseId, page, PAGE_SIZE);
        if (items.length > 0) {
          const pageUrls = Object.fromEntries(items.map((i) => [i.image_id, i.url]));
          mutate((prev) => ({ ...prev, ...pageUrls }), false);
        }
        if (!hasMore) break;
      }
      urlsFetchedAtRef.current = Date.now();
    } catch (error) {
      // Don't update urlsFetchedAtRef so the next refocus retries; surface for diagnostics.
      console.error(`Failed to refresh signed URLs for case ${caseId}:`, error);
    } finally {
      fetchingRef.current = false;
    }
  }, [caseId, api, mutate]);

  useEffect(() => {
    if (!caseId || !urlsCacheKey) return;
    if (Object.keys(urls).length >= safeImages.length) return;
    void refreshSignedUrls();
  }, [caseId, safeImages.length]);

  // Re-mint on refocus if the map is near the TTL, before a lazy <img> requests an expired URL.
  useEffect(() => {
    const onVisible = () => {
      if (
        document.visibilityState === "visible" &&
        urlsFetchedAtRef.current > 0 &&
        Date.now() - urlsFetchedAtRef.current >= URLS_STALE_AFTER_MS
      ) {
        void refreshSignedUrls();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refreshSignedUrls]);

  const imagesWithUrls = safeImages.map((img) => ({
    ...img,
    url: urls[img.image_id],
  }));

  // Re-mint at most once per image so genuinely-missing images don't loop onError → re-mint.
  const handleImageLoadError = useCallback(
    (imageId: string) => {
      if (retriedImageIdsRef.current.has(imageId)) return;
      retriedImageIdsRef.current.add(imageId);
      void refreshSignedUrls();
    },
    [refreshSignedUrls]
  );

  return {
    images: imagesWithUrls,
    isLoading,
    error: error ? getErrorMessage(error) : null,
    handleImageLoadError,
  };
};
